import React, { Component, ReactNode } from 'react';
import ItemToolsMenu from '../../ItemToolsMenu';
import CustomSidebarLayout from '../../custom/CustomSidebarLayout';
import { query, repository, subscribe } from '@trlogic/client-js';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import CollectionNames from '../../../constant/CollectionNames';
import { Group, Role, User } from '../../../domain/rbac/RbacDomains';
import UserListComponent from './UserListComponent';
import CustomLoading from '../../custom/CustomLoading';
import UserEditComponent from './UserEditComponent';
import UserService from '../../../service/UserService';
import { dispatch, RootState } from '../../../redux/store';
import { addPagePath, changePageFilter, changePagePath } from '../../../redux/actions/PageActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import _ from 'lodash';
import CustomPaginator, { CustomPaginatorProps } from '../../custom/CustomPaginator';
import ComponentName from '../../../constant/ComponentName';
import EnvironmentUtil from '../../../util/EnvironmentUtil';
import { CustomFilterFields } from '../../../domain/ui/FilterDomains';
import FilterUtil from '../../../util/FilterUtil';
import ValueFactory from '../../../util/ValueFactory';
import empty = query.empty;
import Filter = query.Filter;
import eq = query.eq;
import { CheckerRequest } from '../../../domain/maker-checker/MakerCheckerDomains';
import and = query.and;
import QueryBuilder = query.QueryBuilder;
import neq = query.neq;
import LanguageUtil from '../../../util/LanguageUtil';

class UserContainer extends Component<Props, State> {
  public state: State = new State();

  private userSubscription: subscribe.Subscription | undefined;
  private groupSubscription: subscribe.Subscription | undefined;
  private rolesSubscription: subscribe.Subscription | undefined;
  private checkerRequestSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private readonly userService: UserService = ServiceContainer.getInstance().get(ServiceNames.USER);
  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly environmentUtil: EnvironmentUtil = InstanceContainer.get(Injectables.ENVIRONMENT_UTIL);

  public render(): ReactNode {
    const { visible, users, loading, selectedUser, checkerRequests, previewUserUpdateRequest, isCreate, roles, groups, rows, filter, currentPage } = this.state;
    if (loading) {
      return <CustomLoading />;
    }
    return (
      <div className={'flex flex-col w-full h-screen lg:h-full px-5'}>
        <div className={'flex min-h-[24px] justify-start bg-white items-center w-full'}>
          <ItemToolsMenu
            createFunc={this.openCreatePage}
            onChangeFilter={this.onChangeFilter}
            filter={filter}
            exportVisible={false}
            defaultFilterDisabled={true}
            importVisible={false}
            createVisible={!this.environmentUtil.isLdapOnly()}
            component={ComponentName.ADMIN_USER}
            componentLabel={LanguageUtil.get({ id: 'pages.admin.user.title', defaultMessage: 'User Management' })}
          />
        </div>
        <div className={'flex w-full h-full'}>
          <div className={'w-full h-full flex flex-col lg:!max-h-[calc(100vh-20rem)] overflow-y-scroll'}>
            <UserListComponent
              selectedUser={selectedUser as User}
              setSelectedUser={this.setSelectedUser}
              updateRequestData={previewUserUpdateRequest}
              setUpdateRequestData={(data: CheckerRequest<User> | undefined) => this.setState({ previewUserUpdateRequest: data })}
              openEditor={this.openUpdatePage}
              users={users}
              checkerRequests={checkerRequests}
              onDeleteUser={this.onDeleteUser}
            />
            <CustomPaginator
              rows={rows}
              currentPage={currentPage}
              currentRecords={users.length}
              rowsPerPageOptions={[1, 5, 10, 20, 50, 200]}
              totalRecords={this.state.totalRecord}
              onPageChange={this.onPageChange}
            />
          </div>
          {visible && <div className={'absolute top-0 left-0 w-full h-full bg-[#D9D9D94D]'}></div>}
          <CustomSidebarLayout
            key='edit'
            content={
              <UserEditComponent
                key='edit'
                onHide={this.closeUpdatePage}
                roleItems={roles}
                groupItems={groups}
                isCreate={isCreate}
                selectedUser={selectedUser as User}
                setSelectedUser={this.setSelectedUser}
                checkerRequests={checkerRequests}
              />
            }
            icon={'people'}
            title={LanguageUtil.get({ id: 'pages.admin.user.editor.title', defaultMessage: 'User Editor' })}
            closable={false}
            visible={visible}
            onHide={this.closeUpdatePage}
          />
          {previewUserUpdateRequest ? (
            <CustomSidebarLayout
              key={'preview'}
              content={
                <UserEditComponent
                  key='edit'
                  onHide={this.closeUpdatePage}
                  roleItems={roles}
                  groupItems={groups}
                  isCreate={isCreate}
                  selectedUser={previewUserUpdateRequest.metadata as User}
                  setSelectedUser={this.setSelectedUser}
                  checkerRequests={checkerRequests}
                  preview
                />
              }
              icon={'people'}
              title={'User'}
              closable={false}
              visible={visible}
              onHide={this.closeUpdatePage}
              position='left'
            />
          ) : null}
        </div>
      </div>
    );
  }

  public componentDidMount() {
    this.prepareUserCount();
    this.prepareFilterFields();
    this.startTracker();
    const { router } = this.props;
    const urlSearchParams = new URLSearchParams(router.location.search);
    const preview: string | null = urlSearchParams.get('preview');
    const id: string | null = urlSearchParams.get('id');
  }

  public componentWillUnmount() {
    this.stopTracker();
  }

  private startTracker = () => {
    const { rows, filter, currentPage } = this.state;
    const userQuery: query.Query = query.QueryBuilder.select()
      .from(CollectionNames.USERS)
      .limit(rows)
      .where(filter)
      .skip((currentPage - 1) * rows)
      .build();
    const groupQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.GROUPS).where(neq('enabled', false)).build();
    const roleQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.ROLES).where(neq('enabled', false)).build();
    const checkerRequestQuery: query.Query = query.QueryBuilder.select()
      .from(CollectionNames.CHECKER_REQUESTS)
      .where(and(eq('component', ComponentName.ADMIN_USER), eq('status', 'Waiting')))
      .build();
    this.userSubscription = subscribe.Formica.subscribe(CollectionNames.USERS, userQuery);
    this.groupSubscription = subscribe.Formica.subscribe(CollectionNames.GROUPS, groupQuery);
    this.rolesSubscription = subscribe.Formica.subscribe(CollectionNames.ROLES, roleQuery);
    this.checkerRequestSubscription = subscribe.Formica.subscribe(CollectionNames.CHECKER_REQUESTS, checkerRequestQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [
      this.userSubscription,
      this.groupSubscription,
      this.rolesSubscription,
      this.checkerRequestSubscription,
    ]);
  };

  private reSubscribe = async () => {
    if (this.computation != undefined) {
      this.computation.onStop(() => this.startTracker());
      await this.stopTracker();
    } else {
      await this.stopTracker();
      this.startTracker();
    }
  };

  private tracker = async () => {
    const { rows, filter } = this.state;
    if (!(this.groupSubscription?.ready() && this.rolesSubscription?.ready() && this.userSubscription?.ready())) {
      return;
    }
    await this.prepareUserCount();
    const groupQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.GROUPS).build();
    const roleQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.ROLES).build();
    const userQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.USERS).limit(rows).where(filter).build();
    const checkerRequestQuery = QueryBuilder.select().from(CollectionNames.CHECKER_REQUESTS).build();
    const groups: Group[] = await this.repository.findMany(groupQuery);
    const roles: Role[] = await this.repository.findMany(roleQuery);
    const users: User[] = await this.repository.findMany(userQuery);
    const checkerRequests: CheckerRequest[] = await this.repository.findMany(checkerRequestQuery);
    this.setState({ groups: groups, roles: roles, users: users, checkerRequests, loading: false });
  };

  private stopTracker = async () => {
    await this.userSubscription?.stop();
    await this.rolesSubscription?.stop();
    await this.groupSubscription?.stop();
    await this.checkerRequestSubscription?.stop();
    this.computation?.stop();
  };

  private prepareFilterFields = async () => {
    const fields: CustomFilterFields[] = await FilterUtil.getFields(ComponentName.ADMIN_USER);
    dispatch(
      changePageFilter({
        filter: ValueFactory.initializeFilter(),
        items: fields,
      }),
    );
  };

  private prepareUserCount = async () => {
    const { filter } = this.state;
    try {
      const _query = query.QueryBuilder.select().from(CollectionNames.USERS).where(filter).build();
      const { count } = await this.userService.getCount(_query);
      this.setState({ totalRecord: count });
    } catch (e) {}
  };

  private onDeleteUser = async (name: string) => {
    await this.userService.deleteUser(name);
    await this.prepareUserCount();
  };

  private setSelectedUser = (user: User) => {
    this.setState({ selectedUser: user });
  };

  private setIsCreate = (isCreate: boolean) => {
    this.setState({ isCreate: isCreate });
  };

  private openCreatePage = () => {
    this.setIsCreate(true);
    this.setState({ visible: true });
  };

  private closeUpdatePage = () => {
    this.setState({ visible: false, selectedUser: undefined });
  };

  private openUpdatePage = () => {
    this.setIsCreate(false);
    this.setState({ visible: true });
  };

  public onPageChange = (event: CustomPaginatorProps) => {
    this.setState({ rows: event.rows, currentPage: event.page }, this.reSubscribe);
  };

  private onChangeFilter = (filter: Filter) => {
    const isSame = _.isEqual(filter, this.state.filter);
    if (isSame) return;
    this.setState({ filter }, this.reSubscribe);
  };
}

interface Props extends ReduxProps, FormicaProps {}

class State {
  users: User[] = [];
  roles: Role[] = [];
  groups: Group[] = [];
  checkerRequests: CheckerRequest[] = [];

  visible = false;
  loading = true;
  isCreate = false;

  rows = 10;
  totalRecord = 10;
  currentPage = 1;
  selectedUser: User | Partial<User> | undefined;
  filter: Filter = empty();
  previewUserUpdateRequest: CheckerRequest<User> | undefined;
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user,
});
const mapDispatchToProps = { addPagePath, changePagePath };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(UserContainer));
